import type {
  OrderAddressDTO,
  OrderShippingAdditionalDataDTO,
  PostNordAddress,
  ShipmentDTO
} from 'ecosystem'
import { isNullable } from 'shared-utils'
import { getPostNordStreetsFromAddress } from 'api/storefront-api/ordersEndpoint/provisionOrder/utils/postnord'

type PostnordDetails = OrderShippingAdditionalDataDTO['postnordDetails']

export interface BuiltShipment {
  shipment: ShipmentDTO
  shippingCost?: number
}

const toOrderAddress = (
  address: PostNordAddress | undefined,
  reference: string
): OrderAddressDTO | undefined =>
  address
    ? {
        address1: getPostNordStreetsFromAddress(address).join(', '),
        address2: '',
        country: address.countryCode || '',
        mobilePhone: '',
        postalCode: address.postCode || '',
        postalRegion: address.city || '',
        attention: '',
        reference
      }
    : undefined

/**
 * Map a PostNord booking/checkout response to the canonical `ShipmentDTO`
 * shape the BE expects on `shipments[0]`. Returns `undefined` when no data
 * is available (caller skips writing a shipment).
 *
 * Used by both the invoice flow (where shipments[] is the only accepted
 * place to put shipping data) and — once the upfront flow migrates —
 * the regular order flow.
 */
export const buildShipmentFromPostnord = (
  postnordResponse?: PostnordDetails
): BuiltShipment | undefined => {
  if (!postnordResponse) return undefined

  if (postnordResponse.isPickupFromStore) {
    const pickupAddress: OrderAddressDTO | undefined = postnordResponse.selectedPickup && {
      address1: postnordResponse.selectedPickup.address.address1 ?? '',
      address2: postnordResponse.selectedPickup.address.address2 ?? '',
      country: postnordResponse.selectedPickup.address.country ?? '',
      mobilePhone: postnordResponse.selectedPickup.address.mobilePhone ?? '',
      postalCode: postnordResponse.selectedPickup.address.postalCode ?? '',
      postalRegion: postnordResponse.selectedPickup.address.postalRegion ?? '',
      attention: postnordResponse.selectedPickup.address.attention ?? '',
      reference: postnordResponse.selectedPickup.address.reference ?? ''
    }

    return {
      shippingCost: 0,
      shipment: {
        shippingProvider: 'POSTNORD',
        shippingType: 'PICKUP',
        // carrierName intentionally omitted for PICKUP: the customer collects
        // from the store directly, PostNord is not involved in the handoff,
        // so the UI should not surface "PostNord" as the carrier.
        ...(pickupAddress
          ? {
              address: pickupAddress,
              locationId: postnordResponse.selectedPickup?.id,
              locationName: postnordResponse.selectedPickup?.name
            }
          : {}),
        ...(postnordResponse.bookingDetails?.bookingId
          ? { shipmentId: postnordResponse.bookingDetails.bookingId }
          : {}),
        ...(postnordResponse.bookingDetails?.trackingNumber
          ? { trackingNumber: postnordResponse.bookingDetails.trackingNumber }
          : {}),
        ...(postnordResponse.bookingDetails?.trackingUrl
          ? { trackingUrl: postnordResponse.bookingDetails.trackingUrl }
          : {}),
        ...(postnordResponse.bookingDetails?.labelUrl
          ? { labelUrl: postnordResponse.bookingDetails.labelUrl }
          : {}),
        additionalInfo: { postnordDetails: postnordResponse }
      }
    }
  }

  const reference = `PostNord ${postnordResponse.deliveryOption?.type ?? ''}`.trim()
  const recipientAddress = toOrderAddress(postnordResponse.recipientAddress, reference)
  const pickupAddress = toOrderAddress(
    postnordResponse.deliveryOption?.details.location?.address,
    reference
  )
  const shippingAddress = pickupAddress ?? recipientAddress

  const carrierService = postnordResponse.deliveryOption?.type
  const trackingNumber = postnordResponse.bookingDetails?.trackingNumber
  const trackingUrl = postnordResponse.bookingDetails?.trackingUrl
  const labelUrl = postnordResponse.bookingDetails?.labelUrl
  const locationId = postnordResponse.deliveryOption?.details.bookingInstructions.servicePointId
  const locationName = postnordResponse.deliveryOption?.details.location?.name
  const shippingCost = postnordResponse.deliveryOption?.price ?? undefined
  const shipmentId = postnordResponse.bookingDetails?.bookingId

  return {
    ...(isNullable(shippingCost) ? {} : { shippingCost }),
    shipment: {
      shippingProvider: 'POSTNORD',
      shippingType: 'DELIVERY',
      carrierName: 'PostNord',
      ...(shipmentId ? { shipmentId } : {}),
      ...(carrierService ? { carrierService } : {}),
      ...(trackingNumber ? { trackingNumber } : {}),
      ...(trackingUrl ? { trackingUrl } : {}),
      ...(labelUrl ? { labelUrl } : {}),
      ...(shippingAddress ? { address: shippingAddress } : {}),
      ...(locationId ? { locationId } : {}),
      ...(locationName ? { locationName } : {}),
      additionalInfo: { postnordDetails: postnordResponse }
    }
  }
}
